import type { Mesh, MeshPhysicalMaterial, MeshStandardMaterial } from 'three';

import type { CutoutManager } from '../cutout-manager.class';
import { loadCSG } from '../../../../utils/lazy-three-csg';
import { createCutoutMeshCfn } from './create-cutout-mesh.cfn';

export interface ApplyCutoutsToMeshParams {
	mesh: Mesh;
	islandIndex: number;
	material?: MeshStandardMaterial | MeshPhysicalMaterial;
}

/**
 * Apply cutouts to a mesh using CSG
 * CSG module is lazy-loaded on first use.
 */
export const applyCutoutsToMeshCfn = async (
	self: CutoutManager,
	{ mesh, islandIndex, material }: ApplyCutoutsToMeshParams
): Promise<Mesh> => {
	const cutouts = self.getCutoutsForIsland(islandIndex);

	if (cutouts.length === 0) {
		return mesh;
	}

	// Lazy load CSG module
	const CSG = await loadCSG();

	let resultMesh = mesh.clone();

	for (const cutout of cutouts) {
		const cutoutMesh = createCutoutMeshCfn({ cutout });

		try {
			// Position cutout mesh
			cutoutMesh.position.set(
				cutout.position.x,
				cutout.position.y,
				cutout.position.z
			);

			if (cutout.rotation) {
				cutoutMesh.rotation.set(
					cutout.rotation.x,
					cutout.rotation.y,
					cutout.rotation.z
				);
			}

			// Update matrices
			resultMesh.updateMatrix();
			cutoutMesh.updateMatrix();

			// Perform CSG subtraction
			const result = CSG.subtract(resultMesh, cutoutMesh);

			// Apply material if provided
			if (material) {
				result.material = material;
			}

			resultMesh = result;
		} catch (error) {
			console.error('CSG operation failed for cutout:', cutout.id, error);
		}
	}

	// Apply shadow settings
	const enableShadows = self.config.qualitySettings?.shadowMapEnabled ?? true;
	resultMesh.castShadow = enableShadows;
	resultMesh.receiveShadow = enableShadows;

	return resultMesh;
};
